import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { INTL_CURRENCIES, type IntlCurrency, isIntlCurrency } from "@/lib/intlPricing";

const STORAGE_KEY = "hm_currency";

type IntlCurrencyContextValue = {
  currency: IntlCurrency;
  setCurrency: (currency: IntlCurrency) => void;
};

const IntlCurrencyContext = createContext<IntlCurrencyContextValue | null>(null);

function detectCurrency(): IntlCurrency {
  if (typeof window === "undefined") return "EUR";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (isIntlCurrency(stored)) return stored;

  const locale = window.navigator.language.toUpperCase();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone.toUpperCase();
  if (locale.includes("US") || timezone.includes("AMERICA/NEW_YORK") || timezone.includes("AMERICA/LOS_ANGELES")) return "USD";
  if (locale.includes("GB") || timezone.includes("EUROPE/LONDON")) return "GBP";
  if (locale.includes("CA") || timezone.includes("CANADA")) return "CAD";
  if (locale.includes("AU") || timezone.includes("AUSTRALIA")) return "AUD";
  return "EUR";
}

export function IntlCurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<IntlCurrency>(() => detectCurrency());

  const setCurrency = (next: IntlCurrency) => {
    setCurrencyState(next);
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, next);
  };

  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, currency);
  }, [currency]);

  const value = useMemo(() => ({ currency, setCurrency }), [currency]);
  return <IntlCurrencyContext.Provider value={value}>{children}</IntlCurrencyContext.Provider>;
}

export function useIntlCurrency() {
  const ctx = useContext(IntlCurrencyContext);
  if (!ctx) throw new Error("useIntlCurrency must be used inside IntlCurrencyProvider");
  return ctx;
}
